import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
  useGetEventBySlugQuery,
  useRsvpToEventMutation,
} from "../api/businessApi";

const EventDetail = () => {
  const { slug } = useParams();
  const { data, isLoading, error } = useGetEventBySlugQuery(slug);
  const [rsvpToEvent, { isLoading: isSubmitting }] = useRsvpToEventMutation();

  const [rsvpForm, setRsvpForm] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const event = data?.event || data;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRsvpForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRsvpSubmit = async (e) => {
    e.preventDefault();
    if (!rsvpForm.name || !rsvpForm.email) {
      Swal.fire({
        title: "Required Fields",
        text: "Please provide your name and email address.",
        icon: "warning",
        confirmButtonColor: "#135879",
      });
      return;
    }

    try {
      await rsvpToEvent({ id: event.id, ...rsvpForm }).unwrap();
      Swal.fire({
        title: "RSVP Confirmed!",
        text: "You have successfully registered for this event.",
        icon: "success",
        timer: 3000,
        showConfirmButton: false,
      });
      setRsvpForm({ name: "", email: "", phone: "", notes: "" });
    } catch (err) {
      Swal.fire({
        title: "RSVP Failed",
        text: err?.data?.message || err?.message || "Failed to register. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc2626",
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Date TBA";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getTypeBadgeClasses = (type) => {
    switch (type?.toLowerCase()) {
      case "virtual":
        return "bg-blue-100 text-blue-800";
      case "hybrid":
        return "bg-purple-100 text-purple-800";
      case "in-person":
      default:
        return "bg-green-100 text-green-800";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#135879]"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-8 text-center">
            <h2 className="text-xl font-bold text-red-700 mb-2">
              Event Not Found
            </h2>
            <p className="text-red-600 mb-4">
              The event you are looking for does not exist or has been removed.
            </p>
            <Link
              to="/events"
              className="text-[#135879] hover:text-[#0e4562] font-medium"
            >
              Back to Events
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link
            to="/events"
            className="inline-flex items-center text-[#135879] hover:text-[#0e4562] font-medium text-sm"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Events
          </Link>
        </nav>

        {/* Event Header */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          {event.image_url && (
            <img
              src={event.image_url}
              alt={event.title}
              className="w-full h-40 sm:h-64 object-cover"
            />
          )}
          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <h1 className="text-3xl font-bold text-[#135879]">
                {event.title}
              </h1>
              <span
                className={`px-4 py-1.5 rounded-full text-sm font-medium ${getTypeBadgeClasses(
                  event.type
                )}`}
              >
                {event.type || "In-Person"}
              </span>
            </div>

            {event.description && (
              <p className="text-gray-600 text-lg mb-6">{event.description}</p>
            )}

            {/* Event Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-[#135879]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-[#135879]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(event.event_date)}
                  </p>
                </div>
              </div>

              {(event.start_time || event.end_time) && (
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-[#135879]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-[#135879]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Time</p>
                    <p className="font-medium text-gray-900">
                      {event.start_time && formatTime(event.start_time)}
                      {event.end_time && ` - ${formatTime(event.end_time)}`}
                    </p>
                  </div>
                </div>
              )}

              {event.location && (
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-[#135879]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-[#135879]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium text-gray-900">
                      {event.location}
                    </p>
                  </div>
                </div>
              )}

              {event.organizer && (
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-[#135879]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-[#135879]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Organizer</p>
                    <p className="font-medium text-gray-900">
                      {event.organizer}
                    </p>
                  </div>
                </div>
              )}

              {event.capacity && (
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-[#135879]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-[#135879]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Capacity</p>
                    <p className="font-medium text-gray-900">
                      {event.rsvp_count || 0} / {event.capacity} registered
                    </p>
                    {event.capacity &&
                      event.rsvp_count >= event.capacity && (
                        <p className="text-sm text-red-600 font-medium">
                          Event is full
                        </p>
                      )}
                  </div>
                </div>
              )}

              {event.category && (
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-[#135879]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-[#135879]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Category</p>
                    <p className="font-medium text-gray-900">
                      {event.category}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {event.registration_link && (
              <a
                href={event.registration_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-[#135879] hover:text-[#0e4562] font-medium text-sm mb-4"
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                External Registration Link
              </a>
            )}
          </div>
        </div>

        {/* Event Content */}
        {event.content && (
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Event Details
            </h2>
            <div
              className="prose max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: event.content }}
            />
          </div>
        )}

        {/* RSVP Form */}
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
          <h2 className="text-xl font-bold text-[#135879] mb-2">
            Register for This Event
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            Fill out the form below to RSVP. No account required.
          </p>

          <form onSubmit={handleRsvpSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={rsvpForm.name}
                  onChange={handleInputChange}
                  required
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#135879] focus:border-[#135879] sm:text-sm"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={rsvpForm.email}
                  onChange={handleInputChange}
                  required
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#135879] focus:border-[#135879] sm:text-sm"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={rsvpForm.phone}
                onChange={handleInputChange}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#135879] focus:border-[#135879] sm:text-sm"
                placeholder="(optional)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={rsvpForm.notes}
                onChange={handleInputChange}
                rows={3}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#135879] focus:border-[#135879] sm:text-sm"
                placeholder="Any dietary requirements, questions, etc. (optional)"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto bg-[#d4af37] hover:bg-[#c19b2e] text-gray-900 font-semibold py-3 px-8 rounded transition duration-300 disabled:opacity-50"
            >
              {isSubmitting ? "Submitting..." : "Register Now"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventDetail;
